import { Editor, Node } from '@tiptap/core';

import { createClassNames } from '../helpers';

const DEFAULT_WIDTH = 200;
const DEFAULT_RATIO = 16 / 9;
const DEFAULT_CONTENT_WIDTH = 400;

export const ClassNames = createClassNames({
  image: true,
  loading: true,
  fadeIn: true,
  error: true,
  dragHandle: true,
  resizing: true,
});

export type ImageOptions = {
  inline: boolean;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    image: {
      /** Add an image */
      setImage: (options: {
        src: string;
        alt?: string;
        title?: string;
        width?: number;
        height?: number;
      }) => ReturnType;
    };
  }
}

type ImageEventHandlers = {
  onLoad: (event: Event) => void;
  onError: (event: ErrorEvent) => void;
};

/**
 * This is our own data structure that we use to store some info about an image.
 */
type Image = {
  src: string;
  element: HTMLImageElement;
  isLoaded: boolean;
  subscribe: (eventHandlers: ImageEventHandlers) => Subscription;
};

/**
 * This describes the internal representation of node attributes, which is
 * similar to but not the same ase what gets output in HTML
 */
type Attributes = {
  id: string;
  src: string;
  alt: string | null;
  title: string | null;
  width: number | null;
  height: number | null;
};

type AttributesDefinition = {
  [K in keyof Attributes]: { default: Attributes[K] };
};

const imageById = new Map<string, Image>();
const mountedCountById = new Map<string, number>();

const createId = createIdGenerator();

export const ResizableImage = Node.create<ImageOptions>({
  name: 'image',

  addOptions() {
    return {
      inline: false,
    };
  },

  inline() {
    return this.options.inline;
  },

  group() {
    return this.options.inline ? 'inline' : 'block';
  },

  draggable: true,

  addAttributes() {
    const attributesDefinition: AttributesDefinition = {
      id: {
        default: '',
      },
      src: {
        default: '',
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      width: {
        default: null,
      },
      height: {
        default: null,
      },
    };
    return attributesDefinition;
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
        getAttrs: (el): Attributes => {
          let img = el as HTMLImageElement;
          return {
            id: createId(),
            src: img.getAttribute('src') ?? '',
            title: img.getAttribute('title'),
            alt: img.getAttribute('alt'),
            width: parseNumber(img.getAttribute('width')),
            height: parseNumber(img.getAttribute('height')),
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    let { src, alt, title, width, height } = HTMLAttributes as Attributes;
    return [
      'img',
      {
        src,
        alt,
        title,
        width: toFixed(width ?? 0),
        height: toFixed(height ?? 0),
        // Makes image conform to parent text alignment in the form view
        style: 'display: inline-block;',
      },
    ];
  },

  addNodeView() {
    return ({ editor, HTMLAttributes, getPos }) => {
      let attributes = HTMLAttributes as Attributes;
      let helpers = getHelpers(editor, attributes, getPos);
      let { id, src } = attributes;
      let existingImage = imageById.get(id);
      let image = existingImage || createImage(src);
      imageById.set(id, image);
      let subscription: Subscription | undefined;
      let span = document.createElement('span');
      span.classList.add(ClassNames.image);
      if (image.isLoaded) {
        helpers.addImageToSpan(span, image.element);
      } else {
        let docWidth = getContentWidth(editor.view.dom);
        // If the markup specifies a width and/or height, we should honor that
        // during loading, if not fallback to some default size.
        let ratio =
          attributes.width && attributes.height
            ? attributes.width / attributes.height
            : DEFAULT_RATIO;
        let width = attributes.width
          ? Math.min(attributes.width, docWidth)
          : DEFAULT_WIDTH;
        let height = width / ratio;
        setSize(span, width, height);
        span.classList.add(ClassNames.loading);
        span.setAttribute('title', t('Loading...'));
        subscription = image.subscribe({
          onLoad: () => {
            span.removeAttribute('title');
            span.classList.remove(ClassNames.loading);
            helpers.addImageToSpan(span, image.element);
          },
          onError: () => {
            span.classList.remove(ClassNames.loading);
            span.classList.add(ClassNames.error);
            span.setAttribute('title', '[' + t('Error loading image') + ']');
          },
        });
      }

      let mountedCount = mountedCountById.get(id) ?? 0;
      mountedCountById.set(id, mountedCount + 1);
      return {
        dom: span,
        update: (_node) => {
          // This will be called after this node's attributes (essentially
          // internal state) is changed. We update attributes after initial
          // image load (if computed size is different than what's already in
          // attributes) and when we finish a drag operation. In both cases we
          // don't need to do anything here because the DOM has already been
          // updated. Returning `true` conveys that we've handled this update
          // and prevents the editor from calling `addNodeView`.
          return true;
        },
        destroy: () => {
          subscription?.unsubscribe();
          // Delay cleaning up the image reference for a few milliseconds
          // because in some cases (e.g. dragging the image to a different part
          // of the document) it will unmount and then remount right afterwards.
          setTimeout(() => {
            let mountedCount = mountedCountById.get(id) ?? 0;
            let newMountedCount = mountedCount - 1;
            if (newMountedCount === 0) {
              mountedCountById.delete(id);
              imageById.delete(id);
            } else {
              mountedCountById.set(id, newMountedCount);
            }
          }, 100);
        },
      };
    };
  },

  addCommands() {
    return {
      setImage: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: {
            id: createId(),
            ...options,
          },
        });
      },
    };
  },
});

type PositionGetter = () => number;

function getHelpers(
  editor: Editor,
  attributes: Attributes,
  getPos: boolean | PositionGetter,
) {
  let { src } = attributes;

  let getPosition = () => (typeof getPos === 'function' ? getPos() : 0);

  let setAttributes = (attributes: Attributes) => {
    let { view } = editor;
    view.dispatch(
      view.state.tr.setNodeMarkup(getPosition(), undefined, attributes),
    );
  };

  return {
    addImageToSpan: (span: HTMLSpanElement, imgEl: HTMLImageElement) => {
      let { width: intrinsicWidth, height: intrinsicHeight } = imgEl;
      let ratio = intrinsicWidth / intrinsicHeight;
      let docWidth = getContentWidth(editor.view.dom);
      // If the attributes contain a width or a height, we should enforce the
      // correct ratio and use those dimensions; else we should use the
      // intrinsic dimensions of the image, adjusted for max-width.
      let width = Math.min(attributes.width ?? intrinsicWidth, docWidth);
      let height = width / ratio;

      setSize(span, width, height);
      if (width !== attributes.width || height !== attributes.height) {
        setAttributes({
          ...attributes,
          width,
          height,
        });
      }
      imgEl.setAttribute('draggable', 'false');
      span.appendChild(imgEl);
      let dragHandle = document.createElement('span');
      dragHandle.classList.add(ClassNames.dragHandle);
      span.appendChild(dragHandle);
      let handlers = createDragHandlers(span, (width, height) => {
        setSize(span, width, height);
        setAttributes({
          ...attributes,
          src,
          width,
          height,
        });
        // Set the image as selected, otherwise the selection is lost after resize.
        editor.commands.setNodeSelection(getPosition());
      });
      dragHandle.addEventListener('mousedown', handlers.onMouseDown);
    },
  };
}

type ResizeCallback = (width: number, height: number) => void;

function setSize(el: HTMLElement, width: number, height: number) {
  Object.assign(el.style, {
    width: px(width),
    height: px(height),
  });
}

function createDragHandlers(span: HTMLSpanElement, onResize: ResizeCallback) {
  let initialSize = { width: 0, height: 0 };
  let initialPosition = { x: 0, y: 0 };
  let computeSize = (event: MouseEvent) => {
    let ratio = initialSize.width / initialSize.height;
    let deltaX = initialPosition.x - event.screenX;
    let newWidth = Math.abs(initialSize.width - deltaX);
    let newHeight = newWidth / ratio;
    return { width: newWidth, height: newHeight };
  };
  let onMouseMove = (event: MouseEvent) => {
    let newSize = computeSize(event);
    setSize(span, newSize.width, newSize.height);
  };
  let onMouseUp = (event: MouseEvent) => {
    document.documentElement.classList.remove(ClassNames.resizing);
    span.removeEventListener('dragstart', onDragStart);
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    let newSize = computeSize(event);
    onResize(newSize.width, newSize.height);
  };
  let onDragStart = (event: DragEvent) => {
    event.preventDefault();
  };
  let onMouseDown = (event: MouseEvent) => {
    let { clientWidth: width, clientHeight: height } = span;
    initialSize = { width, height };
    let { screenX: x, screenY: y } = event;
    initialPosition = { x, y };
    span.addEventListener('dragstart', onDragStart);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.documentElement.classList.add(ClassNames.resizing);
  };
  return { onMouseDown };
}

function createImage(src: string): Image {
  let imgEl = document.createElement('img');
  let isLoaded = false;
  let promise = new Promise<Event>((resolve, reject) => {
    imgEl.addEventListener('load', (event) => {
      isLoaded = true;
      resolve(event);
    });
    imgEl.addEventListener('error', reject);
    imgEl.src = src;
  });
  return {
    src,
    element: imgEl,
    get isLoaded() {
      return isLoaded;
    },
    subscribe: ({ onLoad, onError }) => {
      let isCancelled = false;
      promise.then(
        (event) => {
          !isCancelled && onLoad(event);
        },
        (error) => {
          !isCancelled && onError(error);
        },
      );
      return {
        unsubscribe: () => {
          isCancelled = true;
        },
      };
    },
  };
}

function getContentWidth(el: Element) {
  let computedStyle = getComputedStyle(el);
  let paddingLeft = parsePx(computedStyle.paddingLeft);
  let paddingRight = parsePx(computedStyle.paddingRight);
  let { clientWidth } = el;
  let width = clientWidth - paddingLeft - paddingRight;
  return width || DEFAULT_CONTENT_WIDTH;
}

function parseNumber(value: string | null): number | null {
  if (value == null) {
    return null;
  }
  const parsed = Number(value);
  return isNaN(parsed) ? null : parsed;
}

function toFixed(value: number): string {
  return String(Math.round(value * 10) / 10);
}

function px(value: number): string {
  return toFixed(value) + 'px';
}

function parsePx(value: string): number {
  return value ? parseInt(value, 10) : 0;
}

function createIdGenerator(prefix = '_') {
  let nextId = 0;
  return () => prefix + (nextId++).toString(36);
}
